import { useEffect, useState } from 'react';
import TermsContent from './TermsContent';
import PrivacyContent from './PrivacyContent';

/**
 * Scrollable Terms/Privacy popup — the standard consent-flow pattern:
 * a modal over the current screen rather than navigating away from it
 * (LegalGate, LandingNav). /terms and /privacy still exist as full
 * standalone pages for direct links/sharing; this renders the same
 * content components in a dialog.
 */
interface LegalModalProps {
  initialDoc: 'terms' | 'privacy';
  onClose: () => void;
}

export default function LegalModal({ initialDoc, onClose }: LegalModalProps) {
  const [doc, setDoc] = useState<'terms' | 'privacy'>(initialDoc);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const tabClass = (active: boolean) =>
    `px-3 py-1.5 text-[13px] font-medium rounded-md transition-colors ${
      active
        ? 'bg-legal-green-600 text-white'
        : 'text-text-secondary hover:text-text-primary hover:bg-surface-subtle'
    }`;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 px-4 py-8"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] flex flex-col bg-surface-elevated border border-border-primary rounded-xl shadow-elevation-3"
        role="dialog"
        aria-modal="true"
        aria-label={doc === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border-primary shrink-0">
          <div className="flex gap-1.5">
            <button type="button" onClick={() => setDoc('terms')} className={tabClass(doc === 'terms')}>
              Terms of Service
            </button>
            <button type="button" onClick={() => setDoc('privacy')} className={tabClass(doc === 'privacy')}>
              Privacy Policy
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-text-secondary hover:text-text-primary text-xl leading-none px-2 py-1 rounded-md hover:bg-surface-subtle transition-colors"
          >
            &times;
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-6">
          {doc === 'terms' ? (
            <TermsContent onOpenPrivacy={() => setDoc('privacy')} />
          ) : (
            <PrivacyContent onOpenTerms={() => setDoc('terms')} />
          )}
        </div>
      </div>
    </div>
  );
}
